import { useState, useEffect } from "react";
import labService from "../../api/services/lab.service";
import { API_BASE_URL } from "../../api/config/axios.config";
import "./LabReports.css";

export default function LabReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getFileUrl = (filePath) => {
    if (!filePath) return "";

    if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
      return filePath
        .replace(/^https?:\/\/[^/]+\/uploads\/(https?:\/\/.+)$/i, "$1")
        .replace(/\/uploads\/uploads\/+/i, "/uploads/");
    }

    const uploadsBaseUrl = API_BASE_URL.replace("/api/v1", "");
    const clean = String(filePath)
      .replace(/^\/+/, "")
      .replace(/^uploads\/+/i, "");

    return `${uploadsBaseUrl}/uploads/${clean}`;
  };

  const normalizeLab = (lab) => {
    if (!lab) return null;

    const id = lab.uuid || lab.id || lab._id;
    if (!id) return null;

    return {
      id,
      name: lab.name || "",
      description: lab.description || "",
      type: lab.type || "",
      image: lab.image_url || getFileUrl(lab.image || ""),
      document: lab.document_url || getFileUrl(lab.document || ""),
      uploadDate:
        lab.uploadDate ||
        lab.created_at ||
        lab.createdAt ||
        new Date().toISOString(),
      status:
        typeof lab.is_active === "boolean"
          ? lab.is_active
            ? "ACTIVE"
            : "INACTIVE"
          : lab.status || "ACTIVE",
    };
  };

  const handleThumbError = (e, report) => {
    const img = e.currentTarget;
    const tried = img.getAttribute("data-fallback") === "1";

    if (tried) {
      img.src =
        "data:image/svg+xml;utf8," +
        encodeURIComponent(
          `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='240'><rect width='400' height='240' fill='#f3f4f6'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='#9ca3af'>No image</text></svg>`
        );
      return;
    }

    const current = img.src || "";
    const cleaned =
      current
        .replace(/^https?:\/\/[^/]+\/uploads\/(https?:\/\/.+)$/i, "$1")
        .replace(/\/uploads\/uploads\/+/i, "/uploads/") ||
      (report?.image ? getFileUrl(report.image) : "");

    img.setAttribute("data-fallback", "1");

    if (cleaned && cleaned !== current) {
      img.src = cleaned;
    } else {
      img.src =
        "data:image/svg+xml;utf8," +
        encodeURIComponent(
          `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='240'><rect width='400' height='240' fill='#f3f4f6'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='#9ca3af'>No image</text></svg>`
        );
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await labService.userList({
          page: 0,
          limit: 100,
          search: "",
        });

        const payload = data;
        let items = [];

        if (Array.isArray(payload)) {
          items = payload;
        } else if (Array.isArray(payload.data)) {
          items = payload.data;
        } else if (Array.isArray(payload.data?.items)) {
          items = payload.data.items;
        } else if (Array.isArray(payload.items)) {
          items = payload.items;
        } else {
          const node = payload?.data ?? payload;
          if (Array.isArray(node)) {
            items = node;
          } else if (Array.isArray(node?.docs)) {
            items = node.docs;
          }
        }

        const normalized = items.map(normalizeLab).filter(Boolean);

        const activeOnly = normalized.filter(
          (lab) => (lab.status || "").toUpperCase() === "ACTIVE"
        );

        const mapped = activeOnly.map((lab) => ({
          id: lab.id,
          productName: lab.name,
          batchTitle: lab.description,
          testType: lab.type || "Lab Report",
          testDate: lab.uploadDate,
          image: lab.image,
          document: lab.document,
        }));

        setReports(mapped);
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err.message ||
          "Failed to load lab reports";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const handleDownload = (report) => {
    if (!report?.id) {
      console.error("Download cannot proceed: report has no ID.");
      return;
    }

    // ✅ Use a hidden iframe to trigger the download without opening/closing a new window
    const downloadUrl = `${API_BASE_URL}/lab/download/${report.id}`;

    let iframe = document.getElementById('hidden-download-iframe');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'hidden-download-iframe';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }
    iframe.src = downloadUrl;
  };

  const handleView = async (report) => {
    if (!report?.id) return;

    try {
      const fileUrl = await labService.viewPdf(report.id);
      if (!fileUrl) {
        throw new Error("API did not return a view URL.");
      }
      const secureUrl = fileUrl.replace(/^http:/, "https:");
      // Open PDF in new tab
      window.open(secureUrl, "_blank", "noopener,noreferrer");
    } catch (viewError) {
      console.error("View failed:", viewError);
      alert("Failed to view document. Please try again later.");
    }
  };

  if (loading) {
    return (
      <div className="lab-reports-page">
        <div className="lab-reports-container">
          <p>Loading lab reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lab-reports-page">
        <div className="lab-reports-container">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Large Logo Section - Same as Home */}
      <div
        style={{
          position: "fixed",
          top: "16px",
          left: "24px",
          zIndex: 999,
          display: "flex",
          alignItems: "center",
        }}
      >
        <a
          href="/"
          style={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.filter =
              "drop-shadow(0 0 20px rgba(0, 255, 255, 0.4))";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.filter = "none";
          }}
        ></a>
      </div>

      <div className="lab-reports-page">
        {/* Hero Section */}
        <section className="lab-reports-hero">
          <div className="lab-reports-hero-container">
            <div className="lab-reports-header">
              <h1 className="lab-reports-title">
                Lab-Tested. Professionally <span className="highlight">Verified</span>.
              </h1>
              <div className="lab-reports-underline"></div>
              <p className="lab-reports-subtitle">
                Every batch independently tested for purity, potency, and safety.
              </p>
            </div>
          </div>
        </section>

        {/* Trust Summary Bar */}
        <section className="trust-summary-bar">
          <div className="trust-summary-container">
            <div className="trust-item">
              <span className="trust-icon">✓</span>
              <span>Third-Party Tested</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">✓</span>
              <span>Botanical Ingredients Only</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">✓</span>
              <span>No Synthetic Stimulants</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">✓</span>
              <span>Full Transparency</span>
            </div>
          </div>
        </section>

        {/* Reports Grid Section */}
        <section className="lab-reports-main">
          <div className="lab-reports-container">
            <div className="lab-reports-grid">
              {reports.map((report) => (
                <div key={report.id} className="lab-report-card">
                  {report.image && (
                    <div className="lab-report-image-container">
                      <img
                        src={report.image}
                        alt={report.productName}
                        className="lab-report-image"
                        onError={(e) => handleThumbError(e, report)}
                      />
                    </div>
                  )}

                  <div className="lab-report-content">
                    <h3 className="lab-report-title">{report.productName}</h3>
                    <p className="lab-report-batch">{report.batchTitle}</p>

                    <div className="lab-report-meta">
                      <span className="lab-report-badge">{report.testType}</span>
                      <span className="lab-report-date">
                        Uploaded:{" "}
                        {new Date(report.testDate).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                    </div>

                    <div className="lab-report-tests">
                      <span className="test-item">Purity</span>
                      <span className="test-separator">•</span>
                      <span className="test-item">Potency</span>
                      <span className="test-separator">•</span>
                      <span className="test-item">Safety</span>
                    </div>
                  </div>

                  <div className="lab-report-actions">
                    <button
                      type="button"
                      className="lab-report-view-btn"
                      onClick={() => handleView(report)}
                    >
                      View PDF
                    </button>

                    <button
                      type="button"
                      className="lab-report-download-btn"
                      onClick={() => handleDownload(report)}
                    >
                      Download PDF
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How Our Testing Works Section */}
        <section className="testing-explanation">
          <div className="testing-container">
            <h2 className="testing-title">How Our Testing Works</h2>
            <div className="testing-content">
              <p>Every product batch is tested by independent labs</p>
              <p>Reports are published for full customer transparency</p>
              <p>No fillers, no shortcuts</p>
            </div>
          </div>
        </section>

        {/* Trust Reinforcement Section */}
        <section className="trust-reinforcement">
          <div className="trust-container">
            <div className="trust-grid">
              <div className="trust-card">
                <div className="trust-card-icon">🔬</div>
                <h3>Third-Party Certified Labs</h3>
                <p>
                  Independent laboratory testing ensures unbiased, accurate results you can trust.
                </p>
              </div>

              <div className="trust-card">
                <div className="trust-card-icon">⚗️</div>
                <h3>Pharmaceutical-Grade Standards</h3>
                <p>
                  We maintain the highest standards in testing protocols and quality control measures.
                </p>
              </div>

              <div className="trust-card">
                <div className="trust-card-icon">📋</div>
                <h3>Transparency First Policy</h3>
                <p>
                  Full access to all test results and complete ingredient disclosure for every product.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}


